import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Filter, 
  RefreshCw, 
  BarChart3, 
  Database,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

const AdminDashboard = ({ stats, onNavigate }) => {
  const cards = [
    {
      title: 'Règles de Filtrage',
      description: 'Gérer les règles d\'inclusion et d\'exclusion des villas',
      icon: Filter,
      color: 'from-blue-500 to-blue-600',
      stats: stats?.filteringRules || {},
      action: () => onNavigate('filtering'),
      badge: stats?.filteringRules?.active_rules > 0 ? 'Actif' : 'Aucune règle'
    },
    {
      title: 'Synchronisation Villanovo',
      description: 'Importer et mettre à jour les villas depuis l\'API Villanovo',
      icon: RefreshCw,
      color: 'from-green-500 to-green-600',
      stats: stats?.sync || {},
      action: () => onNavigate('sync'),
      badge: stats?.sync?.lastSync ? `Dernière sync: ${stats.sync.lastSync}` : 'Jamais synchronisé'
    },
    {
      title: 'Base de Données',
      description: 'Statistiques et état de la base de données',
      icon: Database,
      color: 'from-purple-500 to-purple-600',
      stats: stats?.database || {},
      action: () => onNavigate('stats'),
      badge: `${stats?.database?.totalVillas || 0} villas`
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
        <p className="text-gray-600 mt-2">
          Gérez vos villas, règles de filtrage et synchronisations
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Villas Actives</p>
              <p className="text-2xl font-bold text-blue-900">{stats?.database?.totalVillas || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Règles Actives</p>
              <p className="text-2xl font-bold text-green-900">{stats?.filteringRules?.active_rules || 0}</p>
            </div>
            <Filter className="w-8 h-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 font-medium">Villas Filtrées</p>
              <p className="text-2xl font-bold text-amber-900">{stats?.database?.filteredVillas || 0}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-amber-600" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Dernière Sync</p>
              <p className="text-sm font-medium text-purple-900">{stats?.sync?.lastSync || 'Jamais'}</p>
            </div>
            <Clock className="w-8 h-8 text-purple-600" />
          </div>
        </Card>
      </div>

      {/* Main Modules */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card 
              key={index}
              className="p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={card.action}
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{card.title}</h3>
              <p className="text-sm text-gray-600 mb-4">{card.description}</p>
              
              <Badge variant="secondary" className="text-xs">
                {card.badge}
              </Badge>
            </Card>
          );
        })}
      </div>

      {/* Status Alerts */}
      {stats?.alerts && stats.alerts.length > 0 && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-2">Alertes</h4>
              <ul className="space-y-1">
                {stats.alerts.map((alert, index) => (
                  <li key={index} className="text-sm text-yellow-800">• {alert}</li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;

